import { useEffect, useRef } from "react";

// Intercepta el boton/gesto "Atras" del celular mientras un Drawer/bottom-
// sheet esta abierto: en vez de navegar a la pagina anterior (o salir de la
// app), cierra el Drawer y deja "Atras" listo para navegar de verdad la
// proxima vez.
//
// Patron: al abrirse, se empuja una entrada de historial "dummy". Si el
// usuario presiona "Atras", el navegador la consume (dispara popstate) y
// ahi cerramos el Drawer -- no hace falta pushear nada de vuelta, esa
// entrada ya se gasto sola. Si el Drawer se cierra por otro medio (boton
// "Cancelar", tocar afuera, elegir una opcion), la entrada dummy quedaria
// huerfana en el historial -- el cleanup la consume con un history.back()
// silencioso para que el siguiente "Atras" real no pida un toque de mas.
//
// skipCloseHandlingRef (opcional): ref booleano que el caller puede poner en
// true justo antes de una navegacion INTENCIONAL (ej. tocar una fila del
// menu "Mas"). Si esta activo cuando el cleanup corre, el cleanup nunca
// llama history.back() -- sin importar history.state ni el orden de
// ejecucion -- porque ya sabemos con certeza que este cierre es por
// navegacion real, no por "Atras" ni por "Cancelar"/tocar afuera. Reemplaza
// el enfoque anterior (diferir el chequeo con setTimeout para ganarle el
// timing a un navigate() encolado), que dependia del orden de macrotasks.
const useBackButtonClose = (open, onClose, skipCloseHandlingRef) => {
  const onCloseRef = useRef(onClose);
  const pushedRef = useRef(false);

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return undefined;

    window.history.pushState({ drawerOpen: true }, "");
    pushedRef.current = true;

    const handlePopState = () => {
      pushedRef.current = false;
      onCloseRef.current();
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      if (pushedRef.current) {
        pushedRef.current = false;
        if (skipCloseHandlingRef?.current) {
          skipCloseHandlingRef.current = false;
          return;
        }
        // Solo consumir la entrada dummy si seguimos parados en ella. Si
        // mientras el Drawer estaba abierto ocurrio una navegacion real que
        // no marco la bandera anterior, history.state ya no es el nuestro --
        // llamar back() en ese caso deshace esa navegacion real en vez de
        // limpiar el dummy.
        if (window.history.state?.drawerOpen) {
          window.history.back();
        }
      }
    };
  }, [open]);
};

export default useBackButtonClose;
